import { DoorOpen, Thermometer } from 'lucide-react';
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Socket, io } from 'socket.io-client';
import { toast } from 'sonner';

import { customToastOptions } from '@/components/notifications/custom-toast-options';
import { NotificationToastCard } from '@/components/notifications/NotificationToastCard';
import { useAuth } from '@/lib/use-auth';

import { RealtimeContext } from './realtime-context';

// ─── Types ────────────────────────────────────────────────────────────────────

interface CheckinPayload {
  personName: string;
  role: 'student' | 'staff';
  time: string;
  date: string;
}

interface FeverPayload {
  personName: string;
  tempC: number;
  date: string;
}

// ─── Provider ─────────────────────────────────────────────────────────────────

/**
 * Mounts a single Socket.IO connection to /realtime only when the user is
 * authenticated.  The socket is stored in a ref (one per mount) and torn
 * down on unmount.  Cookie auth is handled by the browser automatically via
 * `withCredentials: true` — no token in JS needed.
 */
export function RealtimeProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { t } = useTranslation();
  // The socket effect must not restart on a language change, so it reads the latest `t` here.
  const tRef = useRef(t);
  useEffect(() => {
    tRef.current = t;
  }, [t]);
  const socketRef = useRef<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  // Every live subscription, kept independently of the socket. React runs child
  // effects BEFORE the parent's, so on a fresh page load consumers subscribe while
  // socketRef is still null; attaching straight to the socket silently dropped those
  // handlers for the life of the mount, and the page then only updated by polling.
  // Handlers registered here are (re)attached the moment a socket exists.
  const listenersRef = useRef(new Map<string, Set<(...args: unknown[]) => void>>());

  // Stable subscribe / emit handles so consumers don't need to re-subscribe
  // if the provider re-renders.
  const subscribe = useCallback((event: string, handler: (...args: unknown[]) => void): (() => void) => {
    const forEvent = listenersRef.current.get(event) ?? new Set();
    forEvent.add(handler);
    listenersRef.current.set(event, forEvent);
    socketRef.current?.on(event, handler);

    return () => {
      listenersRef.current.get(event)?.delete(handler);
      socketRef.current?.off(event, handler);
    };
  }, []);

  const emit = useCallback((event: string, payload?: unknown) => {
    socketRef.current?.emit(event, payload);
  }, []);

  useEffect(() => {
    if (!user) return;

    const apiOrigin = (import.meta.env.VITE_API_URL || 'http://localhost:3001/api').replace(/\/api\/?$/, '');
    const socket = io(`${apiOrigin}/realtime`, {
      transports: ['websocket'],
      withCredentials: true,
      // Retry indefinitely (Socket.IO's default, with backoff). A capped attempt count
      // meant one API restart or a laptop sleeping past the retries left the tab
      // permanently disconnected: the user dropped off everyone's presence list and
      // stopped receiving live updates until they reloaded by hand.
      reconnection: true,
    });

    socketRef.current = socket;
    // Attach whatever consumers registered before this effect ran.
    const listeners = listenersRef.current;
    for (const [event, handlers] of listeners) {
      for (const handler of handlers) socket.on(event, handler);
    }

    socket.on('connect', () => setIsConnected(true));
    socket.on('disconnect', () => setIsConnected(false));

    // Global check-in toast — server only sends this to admin/teacher watchers.
    socket.on('checkin', (p: CheckinPayload) => {
      toast.custom(
        id => (
          <NotificationToastCard
            tone="activity"
            icon={DoorOpen}
            title={`${p.personName} checked in`}
            detail={p.time}
            time={null}
            durationMs={3000}
            onDismiss={() => toast.dismiss(id)}
          />
        ),
        customToastOptions(3000)
      );
    });

    // Global fever toast — server emits to medical-watchers.
    socket.on('fever', (p: FeverPayload) => {
      toast.custom(
        id => (
          <NotificationToastCard
            tone="urgent"
            icon={Thermometer}
            title={p.personName}
            detail={tRef.current('notifications.detail.temperature', { tempC: p.tempC })}
            time={null}
            durationMs={8000}
            onDismiss={() => toast.dismiss(id)}
          />
        ),
        customToastOptions(8000)
      );
    });

    return () => {
      for (const [event, handlers] of listeners) {
        for (const handler of handlers) socket.off(event, handler);
      }
      socket.disconnect();
      socketRef.current = null;
      setIsConnected(false);
    };
  }, [user]);

  return <RealtimeContext.Provider value={{ subscribe, emit, isConnected }}>{children}</RealtimeContext.Provider>;
}
